import React from 'react';
import { ReadyState } from 'react-use-websocket';
import useDualChannelAudioStream from '../../../Hooks/audio/useDualChannelAudioStream';
import useIsAppEnabled from '../../ApplicationSyncContext/useIsAppEnabled';
import useAudioConfigContext from '../../AudioConfigContext/useAudioConfigContext';
import useAudioCapture, {
  useAudioCaptureFromStream,
  useRestart,
} from '../Contexts/SessionConnectionContextProvider/Hooks/useAudioCapture';
import { useDualChannelDevices } from '../Contexts/SessionStoreContextProvider/SessionStoreContextProvider';
import useAudioWebsocketApiConnectionContext from '../Contexts/WebsocketConnectionProviders/AudioWebsocketApiConnectionContext/useAudioWebsocketApiConnectionContext';
import useSessionInfo from './useSessionInfo';

function MonoAudioCapture({ children }: { children: React.ReactNode }) {
  const {
    sendMessage, sendJsonMessage, lastJsonMessage, readyState,
  } = useAudioWebsocketApiConnectionContext();
  const audioConfig = useAudioConfigContext();
  const session = useSessionInfo();

  const inProgress = !!session && readyState === ReadyState.OPEN;
  const [lastAudioBlob, restart] = useAudioCapture(inProgress, audioConfig, session?.id);
  const sessionIdRef = React.useRef<string | null>(session?.id ?? null);

  React.useEffect(() => {
    if (session?.id && sessionIdRef.current && session.id !== sessionIdRef.current && readyState === ReadyState.OPEN) {
      restart();
    }
    sessionIdRef.current = session?.id ?? null;
  }, [session?.id, readyState]);

  React.useEffect(() => {
    if (lastJsonMessage?.type === 'restart-audio-stream' && readyState === ReadyState.OPEN) {
      restart();
      sendJsonMessage({ type: 'clear-webm-audio', date: Date.now() * 1e6 });
    }
  }, [lastJsonMessage, readyState]);

  React.useEffect(() => {
    if (lastAudioBlob) {
      sendMessage(lastAudioBlob, 0);
    }
  }, [lastAudioBlob]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

function DualChannelAudioCapture({ children }: { children: React.ReactNode }) {
  const {
    sendMessage, sendJsonMessage, lastJsonMessage, readyState,
  } = useAudioWebsocketApiConnectionContext();
  const session = useSessionInfo();
  const dualChannelDevices = useDualChannelDevices();

  const inProgress = !!session && readyState === ReadyState.OPEN;

  const inputDevice = dualChannelDevices?.input.options.find((d) => d.id === dualChannelDevices.input.selected);
  const outputDevice = dualChannelDevices?.output.options.find((d) => d.id === dualChannelDevices.output.selected);

  const [inputStreamEnabled, restartInputStream] = useRestart(inProgress);
  const [outputStreamEnabled, restartOutputStream] = useRestart(inProgress);

  const inputStream = useDualChannelAudioStream(inputDevice, !!inputStreamEnabled);
  const outputStream = useDualChannelAudioStream(outputDevice, !!outputStreamEnabled);

  const [inputBlob, restartInput] = useAudioCaptureFromStream(
    inputStream,
    !!inputStreamEnabled,
    session?.id,
    restartInputStream,
  );
  const [outputBlob, restartOutput] = useAudioCaptureFromStream(
    outputStream,
    !!outputStreamEnabled,
    session?.id,
    restartOutputStream,
  );

  const sessionIdRef = React.useRef<string | null>(session?.id ?? null);

  React.useEffect(() => {
    if (session?.id && sessionIdRef.current && session.id !== sessionIdRef.current && readyState === ReadyState.OPEN) {
      restartInput();
      restartOutput();
    }
    sessionIdRef.current = session?.id ?? null;
  }, [session?.id, readyState]);

  React.useEffect(() => {
    if (lastJsonMessage?.type === 'restart-audio-stream' && readyState === ReadyState.OPEN) {
      restartInput();
      restartOutput();
      sendJsonMessage({ type: 'clear-webm-audio', date: Date.now() * 1e6 });
    }
  }, [lastJsonMessage, readyState]);

  React.useEffect(() => {
    if (inputBlob) { sendMessage(inputBlob, 0); }
  }, [inputBlob]);

  React.useEffect(() => {
    if (outputBlob) { sendMessage(outputBlob, 1); }
  }, [outputBlob]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

export default function HandleAudioCaptureProvider({ children }: { children: React.ReactNode }) {
  const isAppEnabled = useIsAppEnabled();

  if (isAppEnabled) {
    return <DualChannelAudioCapture>{children}</DualChannelAudioCapture>;
  }
  return <MonoAudioCapture>{children}</MonoAudioCapture>;
}
